import { generateClient, GraphQLResult } from "aws-amplify/api";
import { AwsItems, FieldInstance, FieldTemplate } from "db/definitions";
import { getTableField } from "../db/queries/fieldInstanceQueries";
// @ts-ignore
import { getCellsByTableField } from "../graphql/queries";
import { recordSentry } from "./recordSentry";
import getOptions from "./getOptions";

/**
 * Retrieves every cell that belongs to a table (all pages online, the Dexie index offline).
 * @param id The table instance's id
 * @param online Whether to read from the cloud or from IndexedDB
 * @param options `throwOnError`: rethrow instead of returning what was fetched so far. Callers that are about to
 * rewrite cells based on this list (consolidation) must not act on a partial one.
 */
async function getTableFields(id: string, online: boolean, options?: { throwOnError?: boolean }): Promise<FieldInstance[]> {
	try {
		if (online) {
			const tableFields = await fetchTableFields(id, options?.throwOnError);
			return tableFields;
		} else {
			const field = await getTableField(id);
			return field?.tableFields.items || [];
		}
	} catch (error) {
		if (options?.throwOnError) throw error;
		recordSentry({ error, msg: "Error getting table fields: " });
	}
	return [];
}

/**
 * Given the table's id, retrieves all fields that belong to the table
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id THe id of the table whose fields we want to retrieve
 * @returns All the fields that belong to the table
 */
async function fetchTableFields(id: string, throwOnError = false): Promise<FieldInstance[]> {
	const cells = [];
	interface QueryResponse {
		fieldInstancesByTableField: AwsItems<FieldInstance>;
	}
	try {
		let nextToken: string | null | undefined;
		while (nextToken !== null) {
			const client = generateClient({ authMode: "userPool" });
			const tableFieldsData = (await client.graphql({
				query: getCellsByTableField,
				variables: { tableId: id, nextToken },
				authMode: "userPool",
			})) as GraphQLResult<QueryResponse>;
			for (const cell of tableFieldsData.data.fieldInstancesByTableField.items) {
				let cellCopy: FieldInstance | Promise<FieldInstance> = cell;

				if (cell?.fieldTemplate && cell?.fieldTemplate?.options?.nextToken) {
					// pagination on options required.
					cellCopy = handleCell(cell, cell.fieldTemplate, cell.fieldTemplate.options.nextToken);
				}
				cells.push(cellCopy);
			}
			nextToken = tableFieldsData.data.fieldInstancesByTableField.nextToken;
		}
	} catch (err) {
		if (throwOnError) throw err;
		recordSentry({ error: err, msg: `Error fetching all cells in table ${id}` });
	}
	return await Promise.all(cells);
}

async function handleCell(cell: FieldInstance, fieldTemplate: FieldTemplate, nextToken: string | null = null): Promise<FieldInstance> {
	if (cell?.fieldTemplate?.options?.items) {
		const newOptions = await getOptions(fieldTemplate);
		cell.fieldTemplate!.options.items = newOptions;
	}
	return cell;
}

export default getTableFields;
